/** Canonical client-facing prompts — keep in sync with agent docs and lint-agent-docs.sh */

import { DEFAULT_DISTRIBUTION_DASHBOARD_URL } from './const';

export const CLIENT_CHAT_HEADER = 'COPY TO CLIENT CHAT';
export const AGENT_ONLY_HEADER = 'AGENT ONLY — do not paste below to client';

const BORDER = '═'.repeat(56);

/** Step 5 — Instance ID (INPUT 2) */
export const INPUT_2_CLIENT_CHAT = `Hi! I've finished building your survey app on my side.

To load your real survey questions (instead of demo data), I need one thing from you:

**Your survey instance ID** — a long access token from Sprinklr. It usually looks like three chunks of letters and numbers separated by dots.

**What to do:** Copy that token from Sprinklr and paste it in your next message here.

**What happens next:** I'll connect it, restart the preview, and send you a link to try the survey on your computer.`;

/** Step 7 — Deploy + platform (INPUT 3) */
export const INPUT_4A_CLIENT_CHAT = `Your survey design is ready.

**Would you like to publish it as a live link people can open in their browser?**

**Please reply:** **yes** or **no**

If **yes**, also tell me which hosting you prefer:
- **AWS** (Sprinklr / enterprise CDN — recommended for production surveys)
- **Vercel** (quick hosting alternative)

Example reply: "yes, AWS" or "no"`;

export const CFM_NEXT_STEP_ASK_DEPLOY = 'CFM_NEXT_STEP=ASK_DEPLOY';

export type DeployTargetLabel = 'aws' | 'vercel';

export function formatDeployTargetLabel(target: DeployTargetLabel): string {
  return target === 'vercel' ? 'Vercel' : 'AWS';
}

export function formatInput5ClientChat(surveyId: string): string {
  return `Almost there! One last step to create your **shareable live link**.

I've set everything up on my side. You just need to generate a short code in our distribution dashboard — then paste it back to me and I'll publish the link.

| | |
|---|---|
| **Your Survey ID** | \`${surveyId}\` |
| **Open this dashboard** | ${DEFAULT_DISTRIBUTION_DASHBOARD_URL} |

**What to do:**
1. Open the dashboard link above
2. Click **Create distribution**
3. Paste your **Survey ID** from the table and click **Generate key**
4. Copy the key that appears (a short code like \`a3f91c2e8b4d7016\`)
5. Paste that key in your next message here

**What happens next:** I'll publish your live survey link and send it to you in this chat.

*(You don't need to click Deploy in the dashboard — I'll handle that once you send me the key.)*`;
}

export function formatDistributionDoneClientChat(liveUrl: string): string {
  return `Done! Your survey is live.

**Share this link with participants:**
${liveUrl}

The dashboard also shows this link as **Deployed** with an **Open** button.

Want another link for the same survey (e.g. a second campaign)? Say the word — we'll generate a new key the same way.`;
}

export function formatInput2BClientChat(devUrl: string): string {
  return `Your survey is connected.

**Preview link:** ${devUrl}

**What to do:**
1. Open the link above
2. Click **Let's Get Started** (or **Start Survey**) on the intro page
3. Confirm the survey loads and looks right

Reply **"looks good"** when you're happy with the preview, or tell me what to fix.`;
}

export function formatClientChatBlock(step: string, title: string, body: string): string {
  return [BORDER, `  ${CLIENT_CHAT_HEADER} — ${step}: ${title}`, BORDER, '', body, ''].join('\n');
}

export function formatAgentOnlyBlock(body: string): string {
  return [BORDER, `  ${AGENT_ONLY_HEADER}`, BORDER, '', body, '', BORDER].join('\n');
}

export function formatInput2ClientChatBlock(): string {
  return formatClientChatBlock('Step 5 of 9', 'Connect your survey', INPUT_2_CLIENT_CHAT);
}

export function formatInput2AgentBlock(): string {
  return formatAgentOnlyBlock(
    [
      'After the client pastes their instance ID:',
      '1. Paste into .env / .env.local per instance-id-quick-wire.md',
      '2. Restart the dev server',
      '3. Share preview link (Step 6) after smoke test',
    ].join('\n'),
  );
}

export function formatInput2HandoffBlocks(): string {
  return [formatInput2ClientChatBlock(), formatInput2AgentBlock()].join('\n');
}

export function formatInput2BHandoffBlocks(devUrl: string): string {
  return [
    formatClientChatBlock('Step 6 of 9', 'Preview your survey', formatInput2BClientChat(devUrl)),
    formatAgentOnlyBlock(
      [
        'After client confirms preview:',
        '  npx cfm-sdk sync-from-fetch',
        '  npx cfm-sdk verify-build',
      ].join('\n'),
    ),
  ].join('\n');
}

export function formatInput4AClientChatBlock(): string {
  return formatClientChatBlock('Step 7 of 9', 'Publish live link', INPUT_4A_CLIENT_CHAT);
}

export function formatInput4DeployAgentBlock(): string {
  return formatAgentOnlyBlock(
    [
      'After the client answers deploy + platform:',
      '  npx cfm-sdk set-deploy-target aws|vercel',
      '',
      'If deploy yes + AWS: show Step 8 (distribution key) — do NOT run deploy-to-aws.sh.',
      'If deploy yes + Vercel: npx cfm-sdk deploy',
      'If deploy no: stop — survey stays on localhost only.',
    ].join('\n'),
  );
}

export function formatInput4DeployHandoffBlocks(): string {
  return [formatInput4AClientChatBlock(), formatInput4DeployAgentBlock()].join('\n');
}

export function formatInput5HandoffBlocks(surveyId: string): string {
  return [
    formatClientChatBlock(
      'Step 8 of 9',
      'Generate your distribution key',
      formatInput5ClientChat(surveyId),
    ),
    formatAgentOnlyBlock(
      [
        'Wait for the client to paste the key.',
        'Then run: npx cfm-sdk create-distribution --s3-key=<key>',
        '',
        'Do NOT share any live URL until create-distribution prints ✓ Dashboard updated.',
      ].join('\n'),
    ),
  ].join('\n');
}

export function formatDistributionDoneHandoffBlocks(liveUrl: string): string {
  return formatClientChatBlock(
    'Step 9 of 9',
    'Your survey is live',
    formatDistributionDoneClientChat(liveUrl),
  );
}

export function formatDeployGateFailure(): string {
  return [
    'FAIL: Deploy not confirmed.',
    '',
    'Ask Step 7 client message first (deploy yes/no).',
    'After client says yes + platform, run: npx cfm-sdk set-deploy-target aws|vercel',
  ].join('\n');
}

/** All CLIENT_CHAT bodies for jargon lint in tests */
export function getClientChatBodies(): string[] {
  return [
    INPUT_2_CLIENT_CHAT,
    INPUT_4A_CLIENT_CHAT,
    formatInput5ClientChat('example-survey-id'),
    formatDistributionDoneClientChat('https://example.com/live'),
    formatInput2BClientChat('http://localhost:3000'),
  ];
}
